import {
  getAdapterRecord,
  getOwnedResource,
  getResourceDriver,
  inspectResources,
  type AdapterApplyAnswers,
  type AdapterDefinition,
  type AdapterProject,
  type ReleaseWithAdapterCollections,
} from "@/features/adapters/model";

export const getFocusedConnectionId = (search: string): string | undefined => {
  const connectionId = new URLSearchParams(search).get("connectionId")?.trim();
  return connectionId || undefined;
};

export const getFocusedAttributeId = (search: string): string | undefined => {
  const attributeId = new URLSearchParams(search).get("attributeId")?.trim();
  return attributeId || undefined;
};

export const isAdapterWorkflowPath = (pathname: string): boolean =>
  pathname === "/build" || pathname.startsWith("/build/");

export const autoFollowAdapterDestination = (
  enabled: boolean,
  destination: string | undefined,
  navigate: (destination: string) => void,
): boolean => {
  if (!enabled || !destination) return false;
  navigate(destination);
  return true;
};

/** The page that shows an existing resource, for the resource selectors' View button. */
/** The Entities page with a relationship selected, by its id or (as entity answers store it) its name. */
export const getEntityViewPath = (relationship: string): string =>
  `/build/entities?relationship=${encodeURIComponent(relationship)}`;

export const getResourceViewPath = (kind: "mapping" | "connection" | "route", resourceId: string): string => {
  switch (kind) {
    case "mapping":
      return `/build/mapping?mappingId=${encodeURIComponent(resourceId)}`;
    case "connection":
      return `/build/connections?connectionId=${encodeURIComponent(resourceId)}`;
    case "route":
      // Routes are listed together on the File Processing page's routing tab, which scrolls to this one.
      return `/build/file-processing?fileProcessingTab=routing&routeId=${encodeURIComponent(resourceId)}`;
  }
};

/** The page of a resource that applying `stepId` newly created, when its driver opens created resources. */
export const getCreatedResourceDestination = ({
  before,
  after,
  definition,
  stepId,
  answers,
  project,
}: {
  before: ReleaseWithAdapterCollections;
  after: ReleaseWithAdapterCollections;
  definition: AdapterDefinition;
  stepId: string;
  answers: AdapterApplyAnswers;
  project?: AdapterProject;
}): string | undefined => {
  const beforeId = getOwnedResource(getAdapterRecord(before, definition)?.ownedResources, stepId)?.resourceId;
  const afterId = getOwnedResource(getAdapterRecord(after, definition)?.ownedResources, stepId)?.resourceId;
  if (!afterId || beforeId === afterId) return undefined;
  return inspectResources({ release: after, definition, answers, project })
    .find((inspection) => inspection.stepId === stepId && inspection.resourceId === afterId && getResourceDriver(inspection.definition).openAfterCreate)
    ?.href;
};
